import { useCallback, useEffect, useState } from "react";
import { researchCms } from "@/api/researchCms.service";
import { SearchModal, searchModalStyles } from "@/components/ResearchCms/SearchModal/SearchModal";
import styles from "./BpaPickerField.module.css";

type BpaPickerFieldProps = {
  label: string;
  editionId: string | null;
  editionNamesByEditionId?: Map<string, string>;
  value: string | null;
  selectedLabel?: string | null;
  onChange: (editionBpaId: string | null) => void;
  disabled?: boolean;
  disabledPlaceholder?: string;
  helperText?: string;
};

export function BpaPickerField({
  label,
  editionId,
  editionNamesByEditionId,
  value,
  selectedLabel: selectedLabelProp,
  onChange,
  disabled,
  disabledPlaceholder = "Not available",
  helperText,
}: BpaPickerFieldProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<
    Array<{ id: string; bpaName: string; rankingEntityName: string; editionId: string }>
  >([]);
  const [searching, setSearching] = useState(false);
  const [resolvedLabel, setResolvedLabel] = useState<string | null>(null);

  const displayLabel = selectedLabelProp || resolvedLabel;

  useEffect(() => {
    if (!value || selectedLabelProp) {
      setResolvedLabel(null);
      return;
    }
    let cancelled = false;
    researchCms
      .getEditionBpa(value)
      .then(data => {
        if (cancelled) return;
        const eb = data.editionBpa;
        const edName = editionNamesByEditionId?.get(eb.editionId) ?? "";
        setResolvedLabel([eb.bpaName, eb.rankingEntityName, edName].filter(Boolean).join(" · "));
      })
      .catch(err => {
        console.error("Failed to resolve BPA label:", err);
        if (!cancelled) setResolvedLabel(value);
      });
    return () => {
      cancelled = true;
    };
  }, [value, selectedLabelProp, editionNamesByEditionId]);

  const search = useCallback(
    async (term: string) => {
      if (!editionId) return;
      setSearching(true);
      try {
        const data = await researchCms.getEditionBpas({
          editionId,
          search: term,
          limit: 50,
        });
        setResults(
          data.editionBpas.map(b => ({
            id: b.id,
            bpaName: b.bpaName,
            rankingEntityName: b.rankingEntityName,
            editionId: b.editionId,
          })),
        );
      } catch (err) {
        console.error("BPA search failed:", err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [editionId],
  );

  function handleOpen() {
    if (disabled) return;
    setQuery("");
    setResults([]);
    setOpen(true);
    search("");
  }

  function handleSelect(bpa: {
    id: string;
    bpaName: string;
    rankingEntityName: string;
    editionId: string;
  }) {
    const edName = editionNamesByEditionId?.get(bpa.editionId) ?? "";
    setResolvedLabel([bpa.bpaName, bpa.rankingEntityName, edName].filter(Boolean).join(" · "));
    onChange(bpa.id);
    setOpen(false);
  }

  return (
    <div className={styles.wrapper}>
      <span className={styles.label}>{label}</span>
      <div className={styles.triggerRow}>
        <button type="button" className={styles.trigger} onClick={handleOpen} disabled={disabled}>
          {disabled ? (
            <span className={styles.placeholder}>{disabledPlaceholder}</span>
          ) : value && displayLabel ? (
            <span className={styles.selectedText}>{displayLabel}</span>
          ) : value ? (
            <span className={styles.selectedText}>Loading...</span>
          ) : (
            <span className={styles.placeholder}>Select...</span>
          )}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={styles.chevron}
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
        {value && !disabled && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={() => onChange(null)}
            aria-label={`Clear ${label}`}
          >
            ×
          </button>
        )}
      </div>
      {helperText && <span className={styles.helperText}>{helperText}</span>}

      <SearchModal
        open={open}
        onClose={() => setOpen(false)}
        title={label}
        query={query}
        onQueryChange={setQuery}
        onSearch={search}
        debounceMs={300}
        placeholder="Search practice areas..."
        searching={searching}
        hasResults={results.length > 0}
        resultCount={results.length}
        idleMessage="Search for a practice area"
        idleHint="Results from the selected edition"
      >
        {results.map(bpa => {
          const editionName = editionNamesByEditionId?.get(bpa.editionId) ?? "";
          return (
            <div
              key={bpa.id}
              className={searchModalStyles.resultItem}
              onClick={() => handleSelect(bpa)}
            >
              <div className={searchModalStyles.resultInfo}>
                <span className={searchModalStyles.resultName}>{bpa.bpaName}</span>
                <span className={searchModalStyles.resultSubtitle}>
                  {[bpa.rankingEntityName, editionName].filter(Boolean).join(" · ")}
                </span>
              </div>
            </div>
          );
        })}
      </SearchModal>
    </div>
  );
}
